import React from 'react';
import {
    DateRowStartEnd,
    PriceRow,
    LinkRow,
    SimpleTooltip,
    DateRow,
    Aging,
    TextRow,
    EditWithIcon,
    UnitsRow,
    renderCptCodeAndModifiers,
    TypeRow,
    ManageActions,
    NewCard,
    CommentsPopper, MinLoader,
} from 'components';
import { Checkbox } from '@mui/material';
import { Images, PermissionList, RolePermission, User, apptIssueType, commentModels, KeyPurpose, DownloadFile } from 'utils';
import { AgingRow } from '../../../components/table/tableRows';
import moment from 'moment';

import { ApptStateInfo } from '../../../fragments/schedule/core/modals/apptDetail/apptStateInfo';
import { RefreshClaim } from '../../../fragments/billing/claims/claimDetails/core/refreshClaim';
import { SubmittedClaimStatus } from './core/submittedClaimStatus';
import { APPEAL } from '../../../fragments/billing/claims/claimDetails/constants';
import { defaultStatuses } from '../../client/constants';
import { clearHouseTypeEnums } from '../../../fragments/system/core/constants';
import { renderSecondaryBillingImage, renderSecondaryBillingInfoText } from '../claimPayment';
import { ClaimAdjustmentCell } from './core';
import { OpenClaimDetails } from './openClaimDetails';

const label = { inputProps: { 'aria-label': 'Color switch demo' } };

export const authEnums = {
    WITH_AUTH: 'WITH_AUTH',
    WITHOUT_AUTH: 'WITHOUT_AUTH',
};

export const pendingClaimTableInfo = (navigate, info, handleAction, handleSelect, fullList, selected, afterSuccess) => {

    return {
        loadingType: 'GET_CLAIMS',

        head: [
            RolePermission([PermissionList.CLAIMS_UPDATE?.code]) && {
                renderView: (
                    <SimpleTooltip title={<p>Select All</p>} placement="top-start">
                        <Checkbox
                            {...label}
                            onClick={(e) => handleSelect(e, '', 'all')}
                            name={'checkBox'}
                            style={{ padding: 0, color: '#347AF0' }}
                            checked={fullList?.length === selected?.length}
                        />
                    </SimpleTooltip>
                ),
                width: '35px',
            },
            { name: 'displayId', searchKey: 'DISPLAY_ID', title: 'Claim ID', width: '15%' },
            { name: 'dateRange', title: 'DOS', iconType: 'date', disabled: true, custom: false, width: '20%' },
            { name: 'funder', rowName: 'name', title: 'Payer', disabled: true, width: '20%' },
            { name: 'client', rowName: 'code', title: 'Client Code', disabled: true, width: '16%' },
            { name: 'allowedAmt', title: 'Allowed AMT', disabled: true, iconType: 'date', width: '12%' },
            { name: 'billedAmt', title: 'Billed AMT', disabled: true, iconType: 'date', width: '12%' },
            { name: 'action', title: 'Action', custom: false, width: '100px' },
        ],

        body: (item) => {
            return [
                RolePermission([PermissionList.CLAIMS_UPDATE?.code]) && {
                    value: (
                        <Checkbox
                            {...label}
                            onClick={(e) => handleSelect(e, item)}
                            name={'checkBox'}
                            style={{
                                padding: 0,
                                color: '#347AF0',
                                marginLeft: '-3px',
                                marginRight: '-1px',
                            }}
                            checked={selected?.includes(item?.id)}
                        />
                    ),
                    notClickable: true,
                    width: '35px',
                },
                {
                    value: (
                        <div className="flex-align-center" style={{ gap: 8 }}>
                            <OpenClaimDetails claimId={item?.id} displayId={item?.displayId} afterSuccess={afterSuccess} />
                            <RefreshClaim claimById={item} afterSuccess={afterSuccess} btn={'simple'} />
                        </div>
                    ),
                    notClickable: true,
                    width: '15%',
                },
                { value: <DateRowStartEnd start={item?.dos?.early} end={item?.dos?.latest}  />, width: '20%' },
                {
                    value: (
                        <LinkRow
                            name={item?.funderName ? item.funderName : '--'}
                            href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${item?.funderId}` : ''}
                            checkPermission={item?.funderId ? RolePermission([PermissionList.FS_READ.code]) : null}
                        />
                    ),
                    width: '20%',
                },
                {
                    value: (
                        <LinkRow
                            name={item?.clientFullName ? item?.clientFullName : item?.clientCode ? item?.clientCode : '--'}
                            href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${item?.clientId}` : ''}
                            checkPermission={item?.clientCode ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                        />
                    ),
                    width: '16%',
                },
                { value: <PriceRow info={item?.allowedAmount ? item?.allowedAmount : 0} />, width: '12%' },
                { value: <PriceRow info={item?.billedAmount ? item?.billedAmount : 0} />, width: '12%' },
                {
                    value: <EditWithIcon icon={Images.redTrash} handleClick={() => handleAction(item, 'delete')} />,
                    notClickable: true,
                    width: '100px',
                },
            ];
        },

        renderAccordionRow: (i) => i?.bills,

        accordionHead: [
            { title: 'ID', width: '13%' },
            { title: 'DOS', width: '13%' },
            { title: 'CPT Code', width: '14%' },
            { title: 'Units', width: '10%' },
            { title: 'Billed', width: '10%' },
            { title: 'Allowed', width: '10%' },
            { title: 'Client Total', width: '10%' },
            { title: 'Paid', width: '10%' },
            { title: 'Balance', width: '10%' },
        ],

        accordionBody: (i) => [
            { value: i?.displayId, width: '13%' },
            { value: <DateRow date={i?.dos} />, width: '13%' },
            {
                value: <TextRow name={renderCptCodeAndModifiers(i?.cptCode, i?.chargeRateModifier, i?.chargeRateModifier2, i?.chargeRateModifier3, i?.posModifier)} />,
                width: '14%',
            },
            { value: <UnitsRow info={i?.totalUnits} />, width: '10%' },
            { value: <PriceRow info={i?.totalBilled} />, width: '10%' },
            { value: <PriceRow info={i?.allowedAMT} />, width: '10%' },
            { value: <PriceRow info={i?.clientTotal} />, width: '10%' },
            { value: <PriceRow info={i?.payorPaid} />, width: '10%' },
            { value: <PriceRow info={i?.payorBalance} />, width: '10%' },
        ],

        noDetailsText: 'No bills yet',
        notYet: {
            title: 'No Pending Claims',
            subTitle: 'There are no claims waiting for submission or processing at this time.',
            image: Images.noPendingClaim,
        },
    };
};

// export const pendingClaimHead = (handleSelect, fullList, selected) => {
//     return [
//         RolePermission([PermissionList.CLAIMS_UPDATE?.code]) &&
//         {
//             renderView: <SimpleTooltip
//                 title={<p>Select All</p>}
//                 placement="top-start"
//             >
//                 <Checkbox
//                     {...label}
//                     onClick={(e) => handleSelect(e, '', 'all')}
//                     name={'checkBox'}
//                     style={{ padding: 0, color: '#347AF0' }}
//                     checked={fullList?.length === selected?.length}
//                 />
//             </SimpleTooltip>,
//             width: '50px',
//         },
//         { name: 'displayId', searchKey: 'DISPLAY_ID', title: 'Claim ID', custom: false, width: '150px' },
//         { name: 'dateRange', title: 'DOS', icon: 'date', disabled: true, custom: false, width: '200px' },
//         { name: 'funder', title: 'Payer' },
//         { name: 'client', title: 'Client Code' },
//         { name: 'totalAmount', title: 'Billed Amount', custom: false },
//         { name: 'action', title: 'Action', custom: false, width: '50px' },
//     ];
// };
//
// export const pendingClaimBody = (handleSelect, fullList, selected, afterSuccess, history, info) => {
//     return [
//         RolePermission([PermissionList.CLAIMS_UPDATE?.code]) &&
//         {
//             button: (handle, item) => (
//                 // <SimpleTooltip
//                 //     title={<p>Select</p>}
//                 //     placement="top-start"
//                 // >
//                     <Checkbox
//                         onClick={(e) => handleSelect(e, item)}
//                         disabled={item?.incompleteUnitFlag}
//                         name={'checkBox'}
//                         style={{
//                             padding: 0,
//                             color: '#347AF0',
//                             opacity: item?.incompleteUnitFlag ? 0.5 : 1,
//                         }}
//                         checked={selected?.includes(item?.id)}
//                     />
//                 // </SimpleTooltip>
//             ), notClickable: true,
//         },
//         {
//             rowText: (item) =>
//                 <div className="flex-align-center" style={{ gap: 8 }}>
//
//                     <div onClick={() => navigate(`/claim/${item?.id}`, { tabType: info?.tabType })}>
//                         {item?.displayId}
//                         {/*<ApptFlags*/}
//                         {/*    id={item?.displayId}*/}
//                         {/*    updateFlag={item?.updateFlag}*/}
//                         {/*    incomplete={item?.incompleteUnitFlag}*/}
//                         {/*    removed={item?.removedFromClaimFlag}*/}
//                         {/*/>*/}
//                     </div>
//                     <RefreshClaim claimById={item} afterSuccess={afterSuccess} btn={'simple'} />
//                 </div>, notClickable: true,
//         },
//         { rowText: (item) => <DateRowStartEnd start={item?.dos?.early} end={item?.dos?.latest} /> },
//         {
//             rowText: (item) => <LinkRow
//                 name={item?.funderName ? item.funderName : '--'}
//                 href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${item?.funderId}` : ''}
//                 checkPermission={item?.funderId ? RolePermission([PermissionList.FS_READ.code]) : null}
//             />,
//         },
//         {
//             rowText: (item) => <LinkRow
//                 name={item?.clientCode ? item?.clientCode : '--'}
//                 href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${item?.clientId}` : ''}
//                 checkPermission={item?.clientCode ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
//             />, notClickable: true,
//         },
//         { rowText: (item) => <PriceRow info={item?.billedAmount ? item?.billedAmount : 0} /> },
//         {
//             button: (fn, item) => <EditWithIcon icon={Images.redTrash} handleClick={() => fn('delete')} />,
//             notClickable: true,
//         },
//     ];
// };
//
// export const ACTION_TYPE = 'GET_CLAIMS';
//
// export const pendingClaimNotYet = {
//     title: 'No Pending Claims',
//     subTitle: 'There are no claims waiting for submission or processing at this time.',
//     image: Images.noPendingClaim,
// };

export const billedClaimNotYet = {
    title: 'No Billable Appts',
    subTitle: 'There are no billable appts.',
    image: Images.noPendingClaim,
};

export const claimTabEnums = {
    BILLABLE: 'BILLABLE',
    PENDING: 'PENDING',
    SUBMITTED: 'SUBMITTED',
    COMPLETED: 'COMPLETED',
};
export const claimTabTypes = [
    { title: 'Billable Appt', tabValue: claimTabEnums.BILLABLE },
    { title: 'Pending', tabValue: claimTabEnums.PENDING },
    { title: 'Submitted', tabValue: claimTabEnums.SUBMITTED },

    RolePermission([PermissionList.CLAIMS_READ?.code]) && { title: 'Completed', tabValue: claimTabEnums.COMPLETED },
];

export const generateClaimHead = (handleSelect, fullList, selected, selectAllList) => {
    const { userInfo } = User();

    return [
        {
            renderView: (
                <SimpleTooltip title={<p>Select All</p>} placement="top-start">
                    <Checkbox
                        onClick={(e) => handleSelect(e, '', 'all')}
                        name={'checkBox'}
                        style={{ padding: 0, color: '#0C66E4' }}
                        checked={selectAllList ? true : fullList?.length === selected?.length}
                    />
                </SimpleTooltip>
            ),
            width: '30px',
        },
        { name: '', title: 'DOS', custom: false },
        { name: '', title: 'Auth#', custom: false },
        { name: '', title: 'Payer', custom: false, width: '80px' },
        { name: 'client', title: 'Client', width: '80px' },
        { name: '', title: 'Service', custom: false },
        { name: '', title: 'POS', custom: false },
        { name: '', title: 'Modifier', custom: false, width: '80px' },
        { name: '', title: 'Units/hours', custom: false },
        { name: '', title: 'Bill AMT', custom: false },
        { name: '', title: 'Signature', custom: false, width: '90px' },
        { name: '', title: 'Provider', custom: false },
        RolePermission([PermissionList.CLAIMS_UPDATE?.code]) && { name: '', title: 'Action', custom: false, width: '80px' }, // RolePermission([PermissionList.CLAIMS_UPDATE?.code]) && userInfo?.status === staffStatusEnums?.ACTIVE &&
    ];
};

const issuePermissionMap = {
    [apptIssueType.INCOMPLETE_UNITS]: PermissionList.APPT_BILL_WITH_INCOMPLETE_UNITS?.code,
    [apptIssueType.EXCEEDED_UNITS]: PermissionList.APPT_BILL_WITH_EXCEEDED_UNITS?.code,
    [apptIssueType.AUTHORIZATION_EXPIRED]: PermissionList.APPT_BILL_WITH_EXPIRED_AUTH?.code,
    [apptIssueType.UNIT_OUT_OF_RANGE]: PermissionList.APPT_BILL_WITH_UNIT_OUT_OF_RANGE?.code,
    [apptIssueType.FREQUENCY_LIMIT_EXCEEDED]: PermissionList.APPT_BILL_WITH_FREQUENCY_EXCEEDED?.code,
    // DIAGNOSTIC_CODE_MISSING / DIAGNOSIS_MISMATCH are not listed: they show on the row but never block
    // generation — diagnosis problems are left to the claim validation step.
};

export const checkGenerateBill = (list, bypassPermissions = []) => {
    return list?.some((item) => {
        const permCode = issuePermissionMap[item?.issueType];
        if (!permCode) return false;
        if (bypassPermissions.includes(permCode)) return false;
        return true;
    });
};

export const generateClaimBody = (handleSelect, fullList, selected, handleEdit, loadId, selectAllList, issueBypasses = []) => {
    const { userInfo } = User();

    return [
        {
            button: (handle, item) => {
                const blocked = checkGenerateBill(item?.issues, issueBypasses);
                return (
                    <Checkbox
                        disabled={blocked}
                        onClick={(e) => handleSelect(e, item)}
                        name={'checkBox'}
                        style={{ padding: 0, color: '#0C66E4', opacity: blocked ? 0.5 : 1 }}
                        checked={selectAllList ? !blocked : selected?.includes(item?.id)}
                    />
                );
            },
            notClickable: true,
        },
        {
            rowText: (item) => (
                <div className="claim-dos-and-flag-wrapper" style={{ gap: 8 }}>
                    <div style={{ display: 'flex', marginTop: 2 }}>
                        {item?.locked ? (
                            <img style={{ width: 20, height: 20 }} src={Images.lock} alt={'lock'} />
                        ) : (
                            <img style={{ width: 20, height: 20 }} src={Images.unlock} alt={'unlock'} />
                        )}
                    </div>
                    <div className="claim-dos-and-flag">
                        <p>{moment.utc(item?.startDate).format('MM/DD/YY')}</p>
                    </div>
                    <ApptStateInfo refresh={true} appt={item} related={item?.relatedAppts} />
                </div>
            ),
            notClickable: true,
        },
        {
            rowText: (item) => <TextRow name={item?.authID ? item?.authID : '--'} />,
        },
        {
            rowText: (item) => (
                <SimpleTooltip title={<p>{item?.payerName}</p>} placement="top-start">
                    {RolePermission([PermissionList.FS_READ?.code]) ? (
                        <a
                            target={'_blank'}
                            rel="noopener noreferrer"
                            style={{ color: '#347AF0' }}
                            href={`/fundingSource/${item?.payerId}`}>
                            {item?.payerName?.slice(0, 4)}
                        </a>
                    ) : (
                        <p>{item?.payerName?.slice(0, 4)}</p>
                    )}
                </SimpleTooltip>
            ),
            notClickable: true,
        },
        {
            rowText: (item) => (
                <div className="flex-align-center" style={{ gap: 8 }}>
                    <LinkRow
                        name={item?.client?.fullName ? item?.client?.fullName : item?.client?.code}
                        href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${item?.client?.id}` : ''}
                        checkPermission={item?.client?.id ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                        textWidth={8}
                    />
                    {item?.client?.isFirstBilling && (
                        <SimpleTooltip title={'First time billing for this client'} placement="top">
                            <div>
                                <NewCard />
                            </div>
                        </SimpleTooltip>
                    )}
                </div>
            ),
            notClickable: true,
        },
        {
            rowText: (item) => (
                <TextRow name={item?.cptCode ? `${item?.cptCode} ${item?.chargeRateName ? `(${item?.chargeRateName})` : ''}` : '--'} />
            ),
        },
        {
            rowText: (item) => <TextRow name={`${item?.posName} ${item?.posModifier ? `(${item?.posModifier})` : ''}`} />,
        },
        {
            rowText: (item) => <TextRow name={[item?.chargeRateModifier, item?.chargeRateModifier2, item?.chargeRateModifier3].filter(Boolean).join(', ') || '--'} />,
        },
        {
            rowText: (item) => {
                const incomplete = item?.issues?.find((i) => i?.issueType === apptIssueType.INCOMPLETE_UNITS);
                return (
                    <div style={{ whiteSpace: 'nowrap', gap: 4 }} className="flex-align-center">
                        <p
                            className="billable-appt-units-units"
                            style={
                                incomplete
                                    ? {
                                          background: incomplete ? '#FEF3F2' : '#FFF',
                                          color: incomplete ? '#B42318' : '#363F72',
                                          fontWeight: incomplete ? 600 : 400,
                                          borderColor: incomplete ? 'transparent' : '#D0D5DD',
                                      }
                                    : {}
                            }>
                            {item?.units?.toFixed(1)}
                        </p>
                        <p>({convertToHoursAndMinutes(item?.hours)})</p>
                    </div>
                );
            },
        },
        { rowText: (item) => <PriceRow info={item?.billAmount} /> },
        {
            rowText: (item) =>
                item?.signatureUrl ? (
                    <a
                        className="show-signature-btn"
                        href={item?.signatureUrl}
                        target={'_blank'}
                        rel="noopener noreferrer"
                        style={{ marginTop: 2 }}>
                        <p>View</p>
                        <img src={Images.showAyeBlue} alt={'download'} />
                    </a>
                ) : (
                    <p>N/A</p>
                ),
            notClickable: true,
        },

        {
            rowText: (item) => (
                <LinkRow
                    name={item?.staff?.fullName}
                    href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.staff?.id}` : ''}
                    checkPermission={item?.staff?.id ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                    textWidth={8}
                />
            ),
            notClickable: true,
        },
        RolePermission([PermissionList.CLAIMS_UPDATE?.code]) && { // RolePermission([PermissionList.CLAIMS_UPDATE?.code]) && userInfo?.status === staffStatusEnums?.ACTIVE &&
            rowText: (item) => (
                <EditWithIcon
                    load={loadId === item?.id}
                    disabled={RolePermission([PermissionList.LOCKED_APPT_EDIT?.code]) ? false : item?.locked}
                    styles={RolePermission([PermissionList.LOCKED_APPT_EDIT?.code]) ? {} : item?.locked ? { opacity: 0.5 } : {}}
                    handleClick={() => handleEdit(item)}
                />
            ),
            notClickable: true,
        },
    ];
};

function convertToHoursAndMinutes(decimalHours) {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    return `${formattedHours}h ${formattedMinutes}m`;
}

export const BILLABLE_ACTION_TYPE = 'GET_APPOINTMENT';

/**
 * Clearinghouse a claim was submitted through — the Submitted tab's "Source" column filter.
 *
 * Exported rather than inlined in the head so the SelectedFilters chip in claims.js can label a
 * selection from the same list the column offered it from. The column prepends its own `All` option;
 * that is a control, not a value, so it stays out of here.
 */
export const submittedViaList = [
    { name: 'Availity', id: clearHouseTypeEnums.AVAILITY },
    { name: 'Office Ally', id: clearHouseTypeEnums.OFFICE_ALLY },
    { name: 'Tl Clearinghouse', id: clearHouseTypeEnums.TL_CLEARINGHOUSE },
];

export const billingOverviewHead = [
    { name: 'payerName', title: 'Payer', custom: false },
    { name: 'billableTotal', title: 'Billable Total', custom: false },
    { name: 'pendingClaims', title: 'Pending Claims', custom: false },
    { name: 'openReceivables', title: 'Open Receivables', custom: false },
];

export const billingOverviewBody = (allowed) => {
    const billable = allowed ? 'allowedBillableTotal' : 'billableTotal';
    const pending = allowed ? 'allowedPendingClaims' : 'pendingClaims';
    const open = allowed ? 'allowedOpenReceivables' : 'openReceivables';

    return [
        {
            rowText: (item) => (
                <LinkRow
                    name={item?.payerName ? item.payerName : '--'}
                    href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${item?.payerId}` : ''}
                    checkPermission={item?.payerId ? RolePermission([PermissionList.FS_READ.code]) : null}
                />
            ),
        },
        { rowText: (item) => <PriceRow info={item?.[billable] ? item?.[billable] : 0} /> },
        { rowText: (item) => <PriceRow info={item?.[pending] ? item?.[pending] : 0} /> },
        { rowText: (item) => <PriceRow info={item?.[open] ? item?.[open] : 0} /> },
    ];
};

export const BILLING_OVERVIEW_ACTION_TYPE = 'GET_BILLING_OVERVIEW';

export const billingOverviewNotYet = {
    title: 'No Overviews',
    subTitle: 'There are no Overviews.',
    image: Images.noPendingClaim,
};

export const submittedClaimTableInfo = (navigate, info, handleSelect, handleAction, fullList, selected, getSubmittedClaims, batchView) => {
    return {
        loadingType: 'GET_CLAIMS',
        head: [
            {
                renderView: (
                    <SimpleTooltip title={<p>Select All</p>} placement="top-start">
                        <Checkbox
                            {...label}
                            onClick={(e) => handleSelect(e, '', 'all')}
                            name={'checkBox'}
                            style={{ padding: 0, color: '#347AF0', marginLeft: batchView ? '2px' : '0' }}
                            checked={fullList?.length === selected?.length}
                        />
                    </SimpleTooltip>
                ),
                width: '35px',
            },
            { name: 'displayId', searchKey: 'DISPLAY_ID', title: 'Claim ID', width: '8%' },
            { name: 'dateRange', title: 'DOS', iconType: 'date', disabled: true, width: '6%' },
            { name: 'subDate', title: 'Sub. Date', disabled: true, width: '6%' },
            {
                name: 'submittedVia',
                // title: 'Sub. Source',
                title: 'Source',
                disabled: true,
                width: '8%',
                filterList: [{ name: 'All', id: 'All' }, ...submittedViaList],
            },
            { name: 'funder', rowName: 'name', title: 'Payer', disabled: true, width: '6%' },
            // The office the claim is billed under — see docs/modules/offices.md
            // { name: 'company', title: 'Office', disabled: true, width: '6%' },
            { name: 'client', rowName: 'code', title: 'Client', disabled: true, width: '6%' },
            { name: 'billedAmt', title: 'Billed', disabled: true, iconType: 'date', width: '7%' },
            { name: 'allowedAmt', title: 'Allowed', disabled: true, iconType: 'date', width: '7%' },
            { name: 'paidAmt', title: 'Paid', disabled: true, iconType: 'date', width: '7%' },
            { name: 'clientTotal', title: 'Client Total', disabled: true, iconType: 'date', width: '7%' },
            { name: 'balance', title: 'Balance', disabled: true, iconType: 'date', width: '7%' },

            { name: 'aging', title: 'Aging', iconType: 'date', width: '5%' },
            {
                name: 'clearinghouseStatus',
                title: 'Submission Status',
                disabled: true,
                // Head and body must agree per column, and the percentage columns must total exactly
                // 100 (the 35px checkbox is the only fixed one). This was head 10% / body 15%, which
                // both misaligned the column and pushed the body to 103%.
                width: '12%',
                filterList: [{ name: 'All', id: 'All' }, ...clearinghouseStatusList],
                defaultSelected: defaultStatuses,
            },
            { name: 'action', title: 'Action', custom: false, width: '8%' },
        ],

        body: (item) => {
            return [
                {
                    value: (
                        <Checkbox
                            {...label}
                            onClick={(e) => handleSelect(e, item)}
                            name={'checkBox'}
                            style={{
                                padding: 0,
                                color: '#347AF0',
                                marginLeft: batchView ? '-6px' : '-3px',
                                marginRight: batchView ? 0 : '-1px',
                            }}
                            checked={selected?.includes(item?.id)}
                        />
                    ),
                    notClickable: true,
                    width: '35px',
                },
                {
                    value: (
                        <div className="text-style flex-align-center" style={{ gap: 8 }}>
                            <OpenClaimDetails claimId={item?.id} displayId={item?.displayId} afterSuccess={() => getSubmittedClaims('noLoad')} />

                            {/*<p style={activeStyles} onClick={(e) => handleOpen(e, item?.id)}>*/}
                            {/*    {item?.displayId}*/}
                            {/*</p>*/}

                            <CommentsPopper
                                afterSuccess={() => getSubmittedClaims('noLoad')}
                                buttonClassName="comments-trigger-inline"
                                count={item?.commentCount}
                                title={`Claim ${item?.displayId || ''}`}
                                attachmentPurpose={KeyPurpose.CLAIM_DOC}
                                enableAiImprove
                                resources={[
                                    { onModel: commentModels.Claim, resourceId: item?.id, label: 'Claim' },
                                    ...(item?.bills || []).map((bill) => ({
                                        onModel: commentModels.Bill,
                                        resourceId: bill?.id,
                                        label: bill?.displayId,
                                        // label: `Bill ${i + 1}`,
                                    })),
                                ]}
                            />
                        </div>
                    ),
                    notClickable: true,
                    width: '8%',
                },
                {
                    value: (
                        <div>
                            <p>
                                <DateRow date={item?.dos?.early} />
                            </p>
                            <p>
                                <DateRow date={item?.dos?.latest} />
                            </p>
                        </div>
                    ),
                    width: '6%',
                },

                { value: <DateRow date={item?.submittedDate} />, width: '6%' },
                { value: <TypeRow text={item?.submittedVia ? item?.submittedVia : '--'} />, width: '8%' },
                {
                    value: (
                        <LinkRow
                            name={item?.funderName ? item.funderName : '--'}
                            href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${item?.funderId}` : ''}
                            checkPermission={item?.funderId ? RolePermission([PermissionList.FS_READ.code]) : null}
                        />
                    ),
                    width: '6%',
                },
                // { value: <TextRow name={item?.companyName ? item?.companyName : '--'} />, width: '6%' },
                {
                    value: (
                        <LinkRow
                            name={item?.clientFullName ? item?.clientFullName : item?.clientCode ? item?.clientCode : '--'}
                            href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${item?.clientId}` : ''}
                            checkPermission={item?.clientCode ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                        />
                    ),
                    notClickable: true,
                    width: '6%',
                },
                { value: <PriceRow info={item?.billedAmount ? item?.billedAmount : 0} />, width: '7%' },
                { value: <PriceRow info={item?.allowedAmount ? item?.allowedAmount : 0} />, width: '7%' },

                {
                    value:
                        item?.paymentLinks?.length? (
                            <ManageActions
                                wrapperStyles={{ width: '100%' }}
                                btnClass={'paid-amt-btn'}
                                btnTitle={<PriceRow info={item?.paidAmount} />}
                                renderBody={() =>
                                    !!item?.paymentLinks?.length && (
                                        <div className="column" style={{ alignItems: 'flex-start' }}>
                                            {item?.paymentLinks?.map((i, j) => (
                                                <a
                                                    key={j}
                                                    onClick={(e) => e?.stopPropagation()}
                                                    style={{ marginBottom: 8 }}
                                                    className="paid-amt-btn"
                                                    href={`/claim-payment/${i?.remittanceId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer">
                                                    {i?.remittanceDisplayId}
                                                </a>
                                            ))}
                                        </div>
                                    )
                                }
                            />
                        ) : (
                            <PriceRow info={item?.paidAmount ? item?.paidAmount : 0} />
                        ),
                    width: '7%',
                },

                { value: <PriceRow info={item?.clientTotal ? item?.clientTotal : 0} />, width: '7%' },
                { value: <PriceRow info={item?.remaining ? item?.remaining : 0} />, width: '7%' },
                // { value: <PriceRow info={item?.paidAmount ? item?.paidAmount : 0} />, width: '8%' },
                {
                    value: (
                        <div className="full-width">
                            <AgingRow count={item?.aging} />
                        </div>
                    ),
                    padding: '0',
                    width: '5%',
                },
                { value: <SubmittedClaimStatus info={item} />, notClickable: true, width: '12%' },
                {
                    value: (
                        <div className="flex-align-center" style={{ gap: 8 }}>
                            {item?.status === APPEAL ? (
                                <SimpleTooltip
                                    title={
                                        <div>
                                            <p>This claim is under appeal</p>
                                            <p>Reason: {item?.appealDetails ? item?.appealDetails : '--'}</p>
                                            <p style={{ color: '#5C98EC' }}>(Click to remove from appeal)</p>
                                        </div>
                                    }
                                    placement="top-end">
                                    <EditWithIcon
                                        icon={Images.flag}
                                        id={'appealed-claim'}
                                        handleClick={() => handleAction(item, 'removeFromClaim')}
                                    />
                                    {/*<button className="reset-btn-no-paddings" onClick={() => handleAction(item, 'removeFromClaim')}>*/}
                                    {/*    <img src={Images.appealed} alt="appeal" />*/}
                                    {/*</button>*/}
                                </SimpleTooltip>
                            ) : (
                                <SimpleTooltip title={'Mark this claim as under appeal'} placement="top-end">
                                    {/*<button*/}
                                    {/*    className="reset-btn-no-paddings"*/}
                                    {/*    style={{ cursor: 'pointer' }}*/}
                                    {/*    onClick={() => handleAction(item, 'appeal')}>*/}
                                    {/*    <img src={Images.notAppealed} alt="appeal" />*/}
                                    {/*</button>*/}
                                    <EditWithIcon
                                        icon={Images.secondaryNotBilledFlag}
                                        id={'not-appealed-claim'}
                                        handleClick={() => handleAction(item, 'appeal')}
                                    />
                                </SimpleTooltip>
                            )}

                            {/*{!!item?.secondaryBilling?.secondaryBillingState && (*/}
                            <div>
                                <SimpleTooltip title={renderSecondaryBillingInfoText(item?.secondaryBilling)} placement="top" arrow>
                                    {/*<button*/}
                                    {/*    onClick={() => handleAction(item, 'unMarkSecondaryBilling')}*/}
                                    {/*    className="reset-btn-by-default">*/}
                                    {/*    <img src={renderSecondaryBillingImage(item?.secondaryBilling)} alt="secondary-billed" />*/}
                                    {/*</button>*/}
                                    <EditWithIcon
                                        icon={renderSecondaryBillingImage(item?.secondaryBilling)}
                                        id={'secondary-billing-claim'}
                                        handleClick={() => handleAction(item, 'secondaryBilling')}
                                    />
                                </SimpleTooltip>
                            </div>
                            {/*)}*/}
                        </div>
                    ),
                    notClickable: true,
                    width: '8%',
                },
            ];
        },

        renderAccordionRow: (i) => i?.bills,

        accordionHead: [
            { title: 'ID', width: '10%' },
            { title: 'DOS', width: '10%' },
            { title: 'CPT Code', width: '10%' },
            { title: 'Units', width: '10%' },
            { title: 'Billed', width: '10%' },
            { title: 'Allowed', width: '10%' },
            { title: 'Client Total', width: '10%' },
            { title: 'Paid', width: '10%' },
            { title: 'Adjustments', width: '10%' },
            { title: 'Balance', width: '10%' },
        ],

        accordionBody: (i, j, claim) => [
            { value: i?.displayId, width: '10%' },
            { value: <DateRow date={i?.dos} />, width: '10%' },
            {
                value: <TextRow name={renderCptCodeAndModifiers(i?.cptCode, i?.chargeRateModifier, i?.chargeRateModifier2, i?.chargeRateModifier3, i?.posModifier)} />,
                width: '10%',
            },
            { value: <UnitsRow info={i?.totalUnits} />, width: '10%' },
            { value: <PriceRow info={i?.totalBilled} />, width: '10%' },
            { value: <PriceRow info={i?.allowedAMT} />, width: '10%' },
            { value: <PriceRow info={i?.clientTotal} />, width: '10%' },
            { value: <PriceRow info={i?.payorPaid} />, width: '10%' },
            {
                value: <ClaimAdjustmentCell claim={claim} item={i} afterSuccess={() => getSubmittedClaims('noLoad')} />,
                width: '10%',
                notClickable: true,
            },
            { value: <PriceRow info={i?.payorBalance} />, width: '10%' },
        ],

        noDetailsText: 'No bills yet',
        notYet: {
            title: 'No Billable Appts',
            subTitle: 'There are no billable appts.',
            image: Images.noPendingClaim,
        },
    };
};

/**
 * Adapts a `tableInfo` object (the shape returned by submittedClaimTableInfo:
 * `head` + a value-based `body(item)`) into the `head` / `body` props that the
 * flat <FullTable /> expects. `head` is passed through verbatim, so every column
 * width / filter carries over 1:1 (widths live on the head in FullTable); `body`
 * becomes an array of column descriptors whose `rowText(item)` pulls the matching
 * cell's `value` per row. Per-column flags (notClickable / padding / background)
 * are sampled from the first row — they are constant per column in these configs.
 */
export const tableInfoToFullTable = (tableInfo, list) => {
    const sample = (list || []).find(Boolean);
    const sampleCells = sample ? tableInfo?.body?.(sample) : null;

    return {
        head: tableInfo?.head?.map((i) => ({ ...i, custom: false, noSearch: true, filterList: null })),
        body: (tableInfo?.head || []).map((_, index) => ({
            rowText: (item) => tableInfo?.body?.(item)?.[index]?.value,
            notClickable: sampleCells?.[index]?.notClickable,
            padding: sampleCells?.[index]?.padding,
            background: sampleCells?.[index]?.background,
        })),
    };
};

/**
 * Outer table for the Submitted Claims "Batch View". Each row is a submission
 * batch; its accordion body renders a nested claims table (see submittedClaim.js
 * `renderAccordionDetails`), which in turn expands each claim into its bills.
 */
export const submittedBatchTableInfo = (handleBatchSelect, selectedList, fullList, loadDownload, handleDownload, currentId) => {
    const isBatchSelected = (batch) => {
        const ids = (batch?.claims || []).map((c) => (typeof c === 'string' ? c : c?.id)).filter(Boolean);
        return !!ids.length && ids.every((id) => selectedList?.includes(id));
    };

    return {
        loadingType: 'GET_CLAIM_BATCHES',

        head: [
            { name: 'displayId', title: 'Batch ID', disabled: true, width: '15%' },
            { name: 'subDate', title: 'Sub. Date', disabled: true, width: '10%' },
            { name: 'submittedVia', title: 'Sub. Source', disabled: true, custom: false, width: '15%' },
            { name: 'dos', title: 'DOS', disabled: true, custom: false, width: '15%' },
            { name: 'claimsCount', title: 'Claims Count', disabled: true, custom: false, width: '15%' },
            { name: 'billedAmt', title: 'Total Billed', disabled: true, custom: false, width: '15%' },
            { name: 'batchFile', title: 'Batch File', disabled: true, custom: false, width: '15%' },
        ],

        body: (batch) => [
            { value: <TextRow name={batch?.displayId} />, width: '15%' },
            { value: <DateRow date={batch?.submittedDate} />, width: '10%' },
            { value: <TypeRow text={batch?.submittedVia} />, width: '15%' },
            { value: <DateRowStartEnd start={batch?.dateRangeEarly} end={batch?.dateRangeLatest} />, width: '15%' },
            { value: <TextRow name={batch?.claimCount} />, width: '15%' },
            { value: <PriceRow info={batch?.totalBilledAmount} />, width: '15%' },
            {
                value: (
                    <div>
                        {batch?.file ?
                        <button className="download-1500" onClick={() => handleDownload(batch)}>
                            <p>Download</p>
                            {loadDownload && currentId === batch?.id ? (
                                <MinLoader width={'20px'} height={'20px'} color={'#0C66E4'} margin={'0'} position={'relative'} />
                            ) : (
                                <img style={{ width: 24, height: 24 }} src={Images.downloadCloudBlue} alt="download" />
                            )}
                        </button>
                            :
                            '--'
                        }
                    </div>
                ),
                notClickable: true,
                width: '15%',
            },
        ],

        notYet: {
            title: 'No Submitted Batches',
            subTitle: 'There are no submitted batches for the selected period.',
            image: Images.noPendingClaim,
        },
    };
};


export const billingStatusEnums = {
    PENDING: 'PENDING',
    OPEN: 'OPEN',
    PAID: 'PAID',
    APPEAL:'APPEAL',
    CLOSED: 'CLOSED',
    COMPLETED: 'COMPLETED',
    SUBMITTED: 'SUBMITTED',

    // ACCEPTED: 'ACCEPTED',
    // NOT_DELIVERED: 'NOT_DELIVERED',
    // PENDING_PAYER: 'PENDING_PAYER',
    // ACCEPTED_PAYER: 'ACCEPTED_PAYER',
    // REJECTED_PAYER: 'REJECTED_PAYER',
    // REJECTED: 'REJECTED',
};
export const billingStatusList = [
    { name: 'Submitted', id: billingStatusEnums.SUBMITTED },
    { name: 'Pending', id: billingStatusEnums.PENDING },
    { name: 'Open', id: billingStatusEnums.OPEN },
    { name: 'Paid', id: billingStatusEnums.PAID },
    { name: 'Appeal', id: billingStatusEnums.APPEAL },
    { name: 'Closed', id: billingStatusEnums.CLOSED },
    { name: 'Completed', id: billingStatusEnums.COMPLETED },

    // { name: 'Accepted', id: billingStatusEnums.ACCEPTED },
    // { name: 'Rejected', id: billingStatusEnums.REJECTED },
    // { name: 'Not Delivered', id: billingStatusEnums.NOT_DELIVERED },
    // { name: 'Pending Payer', id: billingStatusEnums.PENDING_PAYER },
    // { name: 'Accepted Payer', id: billingStatusEnums.ACCEPTED_PAYER },
    // { name: 'Rejected Payer', id: billingStatusEnums.REJECTED_PAYER },
];

export const clearinghouseStatusEnums = {
    IN_CH: 'IN_CH',
    CH_REJECTED: 'CH_REJECTED',
    NOT_DELIVERED: 'NOT_DELIVERED',
    PAYER_PRECHECK: 'PAYER_PRECHECK',
    ACCEPTED_BY_PAYER: 'ACCEPTED_BY_PAYER',
    PARTIAL_PMT: 'PARTIAL_PMT',
    DENIED_BY_PAYER: 'DENIED_BY_PAYER',
};
export const clearinghouseStatusList = [
    { name: 'In Clearinghouse', id: clearinghouseStatusEnums.IN_CH },
    { name: 'Clearinghouse Rejected', id: clearinghouseStatusEnums.CH_REJECTED },
    { name: 'Not Delivered', id: clearinghouseStatusEnums.NOT_DELIVERED },
    { name: 'Payer Precheck', id: clearinghouseStatusEnums.PAYER_PRECHECK },
    { name: 'Accepted by Payer', id: clearinghouseStatusEnums.ACCEPTED_BY_PAYER },
    { name: 'Partial Payment', id: clearinghouseStatusEnums.PARTIAL_PMT },
    { name: 'Denied by Payer', id: clearinghouseStatusEnums.DENIED_BY_PAYER },
];



// OpenClaimDetails moved to its own module (./openClaimDetails) so it can be imported
// cheaply without pulling this heavy constants file. Kept re-exported here for back-compat.
export { OpenClaimDetails };
